import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { Plus, Search, Edit, Trash2, FileUp, HelpCircle, ChevronDown, ChevronUp, UserPlus, UserMinus, Megaphone, MegaphoneOff, Power, PowerOff } from 'lucide-react';
import { questionService, QuestionDto, PagedResult } from '@/features/admin/services/questionService';
import { categoryService, CategoryDto } from '@/features/admin/services/categoryService';
import { Select } from '@/components/ui/select';
import { toast } from 'sonner';

type YesNoAll = 'all' | 'yes' | 'no';

function getBulkErrorMessage(error: any): string {
  const d = error?.response?.data;
  if (!d) return error?.message || 'فشل التحديث الجماعي';
  if (typeof d.message === 'string') return d.message;
  if (typeof d.error === 'string') return d.error;
  if (typeof d.detail === 'string') return d.detail;
  if (typeof d.title === 'string') return d.title;
  if (Array.isArray(d.errors) && d.errors[0]?.message) return d.errors[0].message;
  if (Array.isArray(d.errors) && typeof d.errors[0] === 'string') return d.errors[0];
  return 'فشل التحديث الجماعي';
}

export const QuestionListPage = () => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [questions, setQuestions] = useState<PagedResult<QuestionDto> | null>(null);
  const [enableForNewUsersFilter, setEnableForNewUsersFilter] = useState<YesNoAll>('all');
  const [isPromotionFilter, setIsPromotionFilter] = useState<YesNoAll>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [categories, setCategories] = useState<CategoryDto[]>([]);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    categoryService.getAllCategories({ pageSize: 500 }).then((r) => setCategories(r.items)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [searchTerm, enableForNewUsersFilter, isPromotionFilter, categoryFilter]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const enableForNewUsers =
        enableForNewUsersFilter === 'all' ? undefined : enableForNewUsersFilter === 'yes';
      const isPromotionQuestion =
        isPromotionFilter === 'all' ? undefined : isPromotionFilter === 'yes';
      const data = await questionService.getAllQuestions({
        searchTerm: searchTerm || undefined,
        categoryId: categoryFilter || undefined,
        enableForNewUsers,
        isPromotionQuestion,
        pageNumber: 1,
        pageSize: 50,
      });
      setQuestions(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل تحميل الأسئلة');
      console.error('Failed to fetch questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا السؤال؟')) {
      return;
    }

    try {
      await questionService.deleteQuestion(id);
      toast.success('تم حذف السؤال بنجاح');
      fetchQuestions();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل حذف السؤال');
    }
  };

  const handleBulkEnableForNewUsers = async (enable: boolean) => {
    if (filteredQuestions.length === 0) {
      toast.error('لا توجد أسئلة معروضة لتحديثها');
      return;
    }
    try {
      setBulkLoading(true);
      const ids = filteredQuestions.map((q) => q.id);
      const { updatedCount } = await questionService.bulkUpdateEnableForNewUsers(ids, enable);
      toast.success(`تم تحديث ${updatedCount} سؤال`);
      fetchQuestions();
    } catch (error: any) {
      toast.error(getBulkErrorMessage(error));
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkActivate = async (isActive: boolean) => {
    if (filteredQuestions.length === 0) {
      toast.error('لا توجد أسئلة معروضة لتحديثها');
      return;
    }
    try {
      setBulkLoading(true);
      const ids = filteredQuestions.map((q) => q.id);
      const { updatedCount } = await questionService.bulkUpdateIsActive(ids, isActive);
      toast.success(`تم ${isActive ? 'تفعيل' : 'إلغاء تفعيل'} ${updatedCount} سؤال`);
      fetchQuestions();
    } catch (error: any) {
      toast.error(getBulkErrorMessage(error));
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkIsPromotion = async (isPromotion: boolean) => {
    if (filteredQuestions.length === 0) {
      toast.error('لا توجد أسئلة معروضة لتحديثها');
      return;
    }
    try {
      setBulkLoading(true);
      const ids = filteredQuestions.map((q) => q.id);
      const { updatedCount } = await questionService.bulkUpdateIsPromotion(ids, isPromotion);
      toast.success(`تم تحديث ${updatedCount} سؤال`);
      fetchQuestions();
    } catch (error: any) {
      toast.error(getBulkErrorMessage(error));
    } finally {
      setBulkLoading(false);
    }
  };

  const filteredQuestions = questions?.items.filter((q) =>
    q.questionText.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (q.questionTextAr && q.questionTextAr.includes(searchTerm)) ||
    q.categoryName.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-accent-dark mb-2">إدارة الأسئلة</h1>
          <p className="text-accent-dark/70">إدارة وتنظيم أسئلة اللعبة</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/questions/import">
            <Button variant="outline" className="border-accent-greenLight/30">
              <FileUp className="w-4 h-4 mr-2" />
              استيراد Excel
            </Button>
          </Link>
          <Link to="/admin/questions/create">
            <Button className="bg-secondary hover:bg-secondary-dark text-white">
              <Plus className="w-4 h-4 mr-2" />
              إضافة سؤال جديد
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters - Collapsible */}
      <Card className="border-2 border-accent-greenLight/20">
        <button
          type="button"
          onClick={() => setFiltersCollapsed(!filtersCollapsed)}
          className="w-full p-4 flex items-center justify-between text-right hover:bg-accent-greenLight/5 transition-colors rounded-t-lg"
        >
          <span className="font-medium text-accent-dark">الفلاتر والعمليات الجماعية</span>
          {filtersCollapsed ? (
            <ChevronDown className="w-5 h-5 text-accent-dark/60" />
          ) : (
            <ChevronUp className="w-5 h-5 text-accent-dark/60" />
          )}
        </button>
        {!filtersCollapsed && (
          <CardContent className="p-4 pt-0 border-t border-accent-greenLight/10">
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-accent-dark/70 whitespace-nowrap">الفئة:</span>
                  <Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="min-w-[180px] border-accent-greenLight/30"
                  >
                    <option value="">الكل</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.nameAr || cat.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="flex-1 min-w-[200px] relative">
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-accent-dark/50 w-5 h-5" />
                  <Input
                    placeholder="بحث في الأسئلة..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pr-10"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-accent-dark/70">تفعيل للمستخدمين الجدد:</span>
                  <div className="flex gap-1">
                    {(['all', 'yes', 'no'] as const).map((value) => (
                      <Button
                        key={value}
                        variant={enableForNewUsersFilter === value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEnableForNewUsersFilter(value)}
                        className={
                          enableForNewUsersFilter === value
                            ? 'bg-secondary hover:bg-secondary-dark text-white'
                            : 'border-accent-greenLight/30'
                        }
                      >
                        {value === 'all' ? 'الكل' : value === 'yes' ? 'نعم' : 'لا'}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-accent-dark/70">سؤال ترويج:</span>
                  <div className="flex gap-1">
                    {(['all', 'yes', 'no'] as const).map((value) => (
                      <Button
                        key={value}
                        variant={isPromotionFilter === value ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setIsPromotionFilter(value)}
                        className={
                          isPromotionFilter === value
                            ? 'bg-secondary hover:bg-secondary-dark text-white'
                            : 'border-accent-greenLight/30'
                        }
                      >
                        {value === 'all' ? 'الكل' : value === 'yes' ? 'نعم' : 'لا'}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
              {filteredQuestions.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-accent-greenLight/10">
                  <span className="text-sm text-accent-dark/70 self-center">عمليات على المعروضة ({filteredQuestions.length}):</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={bulkLoading}
                    onClick={() => handleBulkActivate(true)}
                    className="border-accent-greenLight/30"
                  >
                    <Power className="w-4 h-4 mr-1" />
                    تفعيل المعروضة (بدون مراجعة)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={bulkLoading}
                    onClick={() => handleBulkActivate(false)}
                    className="border-accent-greenLight/30"
                  >
                    <PowerOff className="w-4 h-4 mr-1" />
                    إلغاء تفعيل المعروضة
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={bulkLoading}
                    onClick={() => handleBulkEnableForNewUsers(true)}
                    className="border-accent-greenLight/30"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    تفعيل الكل للمستخدمين الجدد
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={bulkLoading}
                    onClick={() => handleBulkEnableForNewUsers(false)}
                    className="border-accent-greenLight/30"
                  >
                    <UserMinus className="w-4 h-4 mr-1" />
                    إلغاء تفعيل الكل
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={bulkLoading}
                    onClick={() => handleBulkIsPromotion(true)}
                    className="border-accent-greenLight/30"
                  >
                    <Megaphone className="w-4 h-4 mr-1" />
                    تفعيل كسؤال ترويج للكل
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={bulkLoading}
                    onClick={() => handleBulkIsPromotion(false)}
                    className="border-accent-greenLight/30"
                  >
                    <MegaphoneOff className="w-4 h-4 mr-1" />
                    إلغاء سؤال الترويج للكل
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Questions List */}
      {filteredQuestions.length === 0 ? (
        <Card className="border-2 border-accent-greenLight/20">
          <CardContent className="p-12 text-center">
            <HelpCircle className="w-16 h-16 mx-auto mb-4 text-accent-dark/30" />
            <p className="text-lg text-accent-dark/70 mb-2">لا توجد أسئلة</p>
            <p className="text-sm text-accent-dark/50 mb-4">
              {searchTerm ? 'لم يتم العثور على أسئلة تطابق البحث' : 'ابدأ بإضافة سؤال جديد أو استيراد من Excel'}
            </p>
            <div className="flex gap-2 justify-center">
              <Link to="/admin/questions/import">
                <Button variant="outline" className="border-accent-greenLight/30">
                  <FileUp className="w-4 h-4 mr-2" />
                  استيراد من Excel
                </Button>
              </Link>
              <Link to="/admin/questions/create">
                <Button className="bg-secondary hover:bg-secondary-dark text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  إضافة سؤال جديد
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredQuestions.map((question) => (
            <Card key={question.id} className="border-2 border-accent-greenLight/20">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg font-bold text-accent-dark mb-2">
                      {question.questionTextAr || question.questionText}
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-accent-dark/60">
                      <span>الفئة: {question.categoryName}</span>
                      <span>•</span>
                      <span>الصعوبة: {question.difficulty}</span>
                      {question.enableForNewUsers && (
                        <span className="px-2 py-0.5 text-[10px] bg-secondary/20 text-secondary rounded">للمستخدمين الجدد</span>
                      )}
                      {question.isPromotionQuestion && (
                        <span className="px-2 py-0.5 text-[10px] bg-accent-yellowGreen/30 text-accent-greenDark rounded">ترويج</span>
                      )}
                      {!question.isActive && (
                        <>
                          <span>•</span>
                          <span className="text-accent-red">معطل</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* الإجابة */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-accent-dark">الإجابة:</span>
                    <span className="px-3 py-1 bg-secondary/10 text-secondary font-bold rounded-lg">
                      {question.options?.find(o => o.isCorrect)?.textAr || 
                       question.options?.find(o => o.isCorrect)?.text || 
                       'غير محددة'}
                    </span>
                  </div>
                  {question.explanation && (
                    <div className="bg-accent-lightGray/50 rounded-lg p-3">
                      <p className="text-sm text-accent-dark/70">
                        <span className="font-medium">الشرح:</span> {question.explanation}
                      </p>
                    </div>
                  )}
                  <div className="flex gap-2 pt-2 border-t border-accent-greenLight/20">
                    <Link to={`/admin/questions/edit/${question.id}`} className="flex-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full hover:bg-accent-greenLight/20"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        تعديل
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-1 hover:bg-accent-red/10 text-accent-red"
                      onClick={() => handleDelete(question.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      حذف
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
